"use client";

import Image from "next/image";
import { useState, type MouseEvent } from "react";
import { Link } from "@/i18n/routing";
import { ejEduAuthorRoute } from "@/lib/routes";
import { useToggleToast } from "@/lib/toast";
import { EJEDU_COLORS, EJEDU_TYPE } from "@/lib/theme/ej-education";
import type { Author } from "@/fixtures/ej-education/fixtures";

interface Props {
  author: Author;
  /**
   * Render the avatar and name as links. Default true.
   *
   * MUST be false wherever the chip sits inside another anchor — `<a>` inside
   * `<a>` is invalid HTML, the browser reparents it, and React then reports a
   * hydration mismatch (#418) because the server markup and the parsed DOM no
   * longer agree. `SidebarThumb` wraps its whole row in a link, which is
   * exactly that case.
   */
  linked?: boolean;
  size?: number;
  variant?: "default" | "onDark" | "elevated";
  showFollow?: boolean;
  subtitle?: string;
}

export function AuthorChip({
  author,
  size = 28,
  variant = "default",
  showFollow = false,
  subtitle,
  linked = true,
}: Props) {
  const onDark = variant === "onDark";
  const elevated = variant === "elevated";
  const nameColor = onDark ? EJEDU_COLORS.textOnDark : EJEDU_COLORS.dark;

  if (elevated) {
    return (
      <div className="flex items-center gap-3">
        {/* Avatar and name both open the writer's page — QA: "clicking on the
            author's avatar and name should redirect to the author details
            page". Two anchors rather than one wrapping both, so the follow
            button beside them is not swallowed by a link. */}
        <Avatar
          author={author}
          size={56}
          linked={linked}
          className="shrink-0"
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <Name
            author={author}
            linked={linked}
            style={{ ...EJEDU_TYPE.cardTitleSm, color: EJEDU_COLORS.dark }}
          />
          <span
            style={{
              ...EJEDU_TYPE.meta,
              color: EJEDU_COLORS.textMuted,
              letterSpacing: "0.04em",
              textTransform: "none",
            }}
          >
            {subtitle ?? "專欄作者"}
          </span>
        </span>
        {showFollow && <FollowButton />}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Avatar author={author} size={size} linked={linked} />
      <Name
        author={author}
        linked={linked}
        style={{ ...EJEDU_TYPE.meta, color: nameColor, letterSpacing: "0.04em" }}
      />
      {showFollow && <FollowButton />}
    </span>
  );
}

/**
 * The 關注 toggle on its own.
 *
 * Exported because the author page needs the control WITHOUT the avatar-and-name
 * chip around it: that page already shows the writer at 96px, so rendering a
 * whole second chip at 28px just to reach this button printed the same person
 * twice. QA: "remove the small avatar image, name and follow button; add follow
 * button next to the big author name".
 */
export function AuthorFollowButton() {
  return <FollowButton />;
}

function FollowButton() {
  const [followed, setFollowed] = useState(false);
  const notify = useToggleToast();

  /*
    Was stateless: the label always read "+ 關注" and clicking did nothing but
    re-run a JS hover. Now it toggles, and raises the same localised toast every
    other follow control on the site uses.

    `next` is computed OUTSIDE the state updater on purpose — React runs
    updaters twice under StrictMode (`reactStrictMode: true`), so a toast raised
    inside one fires twice. `lib/toast.ts` documents the same trap.
  */
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    // These chips sit inside article links; a follow must not open the article.
    e.preventDefault();
    e.stopPropagation();
    const next = !followed;
    setFollowed(next);
    notify.follow(next);
  };

  return (
    <button
      type="button"
      aria-pressed={followed}
      onClick={onClick}
      // Resting colours on custom properties, not inline: the hover used to be
      // two JS handlers rewriting `style`, which skipped keyboard focus and
      // `prefers-reduced-motion` entirely.
      className="inline-flex items-center justify-center border border-[color:var(--rest-border)] bg-[color:var(--rest-bg)] text-[color:var(--rest-fg)] transition-colors duration-150 hover:border-[color:var(--hover-bg)] hover:bg-[color:var(--hover-bg)] hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
      style={
        {
          ...EJEDU_TYPE.button,
          fontSize: 11,
          letterSpacing: "0.12em",
          paddingLeft: 10,
          paddingRight: 10,
          paddingTop: 4,
          paddingBottom: 4,
          ["--rest-bg" as string]: followed ? EJEDU_COLORS.primary : "transparent",
          ["--rest-fg" as string]: followed
            ? EJEDU_COLORS.textOnDark
            : EJEDU_COLORS.primary,
          ["--rest-border" as string]: EJEDU_COLORS.primary,
          ["--hover-bg" as string]: followed
            ? EJEDU_COLORS.primaryDark
            : EJEDU_COLORS.primary,
          ["--hover-fg" as string]: EJEDU_COLORS.textOnDark,
        } as React.CSSProperties
      }
    >
      {followed ? "已關注" : "+ 關注"}
    </button>
  );
}

/** Avatar, linked to the writer's page unless nested inside another anchor. */
function Avatar({
  author,
  size,
  linked,
  className = "",
}: {
  author: Author;
  size: number;
  linked: boolean;
  className?: string;
}) {
  const box = (
    <Image src={author.avatar} alt={author.name} fill className="object-cover" unoptimized />
  );
  const style = {
    width: size,
    height: size,
    backgroundColor: EJEDU_COLORS.surfaceAlt,
  };
  if (!linked)
    return (
      <span
        className={`relative inline-block shrink-0 overflow-hidden rounded-full ${className}`}
        style={style}
      >
        {box}
      </span>
    );
  return (
    <Link
      href={ejEduAuthorRoute(author.name)}
      aria-label={author.name}
      className={`hkej-tap relative inline-block shrink-0 overflow-hidden rounded-full transition-opacity hover:opacity-90 motion-reduce:transition-none ${className}`}
      style={style}
    >
      {box}
    </Link>
  );
}

/** The byline, same rule as the avatar. */
function Name({
  author,
  linked,
  style,
}: {
  author: Author;
  linked: boolean;
  style: React.CSSProperties;
}) {
  if (!linked) return <span style={style}>{author.name}</span>;
  return (
    <Link href={ejEduAuthorRoute(author.name)} className="hkej-tap w-fit">
      <span className="hkej-tap-title" style={style}>
        {author.name}
      </span>
    </Link>
  );
}
